import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  FormControl,
  MenuItem,
  Popover,
  Box,
  Typography,
  Divider,
  IconButton,
  Menu,
  ListItemIcon,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AppAlert from "./AppAlert";
import CreateActionIcon from "./CreateActionIcon";
import ProfileCompletion from "./ProfileCompletion";
import useScrollFade from "../hooks/useScrollFade";
import {
  getClientDisplayName,
  getClientId,
  getClientCompletion,
} from "../../core/utils/clientDisplay";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../pages/Home/constants/homeGlass";
import { useAuth } from "../../app/context/AuthContext";
import { MODULE_LIST } from "../../constants/commons";

const NEW_CLIENT_VALUE = "__new_client__";
const CLIENT_SCROLL_THRESHOLD = 5;
const CLIENT_ROW_HEIGHT = 42;
const CLIENT_MENU_BACKGROUND_COLOR = "rgba(20, 23, 19, 0.58)";
const CLIENT_MENU_BACKGROUND_IMAGE =
  "radial-gradient(circle at 18% 0%, rgba(255,255,255,0.18), transparent 32%), linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))";

const thinScrollbarSx = {
  "&::-webkit-scrollbar": { width: "5px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.28)",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(255,255,255,0.45)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,255,255,0.28) transparent",
};

const BORDER_SWEEP_GRADIENT =
  "linear-gradient(to right, transparent, rgba(219, 165, 41, 0.85), transparent)";

const BORDER_SWEEP_TRANSITION = {
  duration: 2.5,
  repeat: Infinity,
  ease: "linear",
};

export default function AppDropdown({
  options = [],
  value = null,
  onChange,
  onNewClient,
  onEditClient,
  disabled = false,
  emphasized = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [clientMenuAnchor, setClientMenuAnchor] = useState(null);
  const [clientMenuTarget, setClientMenuTarget] = useState(null);
  const triggerRef = React.useRef(null);

  const clientOptions = Array.isArray(options) ? options : [];
  const shouldScrollClients = clientOptions.length > CLIENT_SCROLL_THRESHOLD;
  const clientListScroll = useScrollFade({ disabled: !shouldScrollClients || !isOpen });
  const selectedId = getClientId(value);

  const { userPermissions } = useAuth();

  
  const clientModule = Object.hasOwn(userPermissions, MODULE_LIST.CLIENT) ? userPermissions[MODULE_LIST.CLIENT] : false;

  let canViewClients = false;
  let canEditClients = false;
  if (clientModule && (clientModule.edit || clientModule.view)) {
    canViewClients = true;
    canEditClients = clientModule.edit;
  }
  


  const selectedClient = useMemo(
    () =>
      clientOptions.find((opt) => String(getClientId(opt)) === String(selectedId)),
    [clientOptions, selectedId]
  );
  const selectedClientName = getClientDisplayName(selectedClient, "Select Client");

  if (!canViewClients) {
    return null;
  }
  const openNewClientModal = () => {
    setIsOpen(false);
    if (onNewClient) onNewClient();
  };

  const openDropdown = () => {
    if (disabled) return;
    setMenuWidth(triggerRef.current?.offsetWidth ?? null);
    setIsOpen(true);
  };

  const handleClientClick = (client) => {
    if (String(getClientId(client)) === String(selectedId)) {
      if (onChange) onChange(null);
    } else if (onChange) {
      onChange(client);
    }
    setIsOpen(false);
  };

  const closeClientMenu = () => {
    setClientMenuAnchor(null);
    setClientMenuTarget(null);
  };

  const handleOpenClientMenu = (event, client) => {
    event.stopPropagation();
    event.preventDefault();
    const rowEl = event.currentTarget?.closest?.("[data-client-row]");
    setClientMenuAnchor(rowEl || event.currentTarget);
    setClientMenuTarget(client);
  };

  const handleClientMenuEdit = (event) => {
    event.stopPropagation();
    const client = clientMenuTarget;
    closeClientMenu();
    setIsOpen(false);
    onEditClient?.(client);
  };

  const sharedGlass = {
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.065))",
    backdropFilter: "blur(26px) saturate(118%)",
    WebkitBackdropFilter: "blur(26px) saturate(118%)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
  };

  return (
    <>
      <FormControl sx={{ width: "100%", maxWidth: 188, minWidth: 0, flexShrink: 0 }}>
        <Box sx={{ position: "relative", width: "100%" }}>
          {emphasized && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: "-1px",
                overflow: "hidden",
                borderRadius: "14px",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={BORDER_SWEEP_TRANSITION}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: 1.5,
                  width: "100%",
                  background: BORDER_SWEEP_GRADIENT,
                }}
              />
              <motion.div
                animate={{ x: ["100%", "-100%"] }}
                transition={BORDER_SWEEP_TRANSITION}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  height: 1.5,
                  width: "100%",
                  background: BORDER_SWEEP_GRADIENT,
                }}
              />
            </Box>
          )}

        <Box
          ref={triggerRef}
          component="button"
          type="button"
          disabled={disabled}
          onClick={openDropdown}
          sx={{
              position: "relative",
              zIndex: 0,
            ...sharedGlass,
            color: "white",
            borderRadius: "13px",
            height: "34px",
            width: "100%",
            p: 0,
            display: "block",
            boxSizing: "border-box",
            border: emphasized
                ? "1px solid rgba(219, 165, 41, 0.45)"
                : "1px solid rgba(255, 255, 255, 0.16)",
            cursor: disabled ? "default" : "pointer",
            font: "inherit",
            textAlign: "left",
            background:
              "radial-gradient(circle at 14% 12%, rgba(255,255,255,0.16), transparent 28%), linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.065))",
              boxShadow: emphasized
                ? "inset 0 1px 0 rgba(255,255,255,0.24), 0 10px 22px rgba(0,0,0,0.18)"
                : "inset 0 1px 0 rgba(255,255,255,0.22), 0 10px 22px rgba(0,0,0,0.16)",
            transition:
              "border-color 180ms ease, box-shadow 180ms ease, background 180ms ease",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.28)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.28), 0 12px 26px rgba(0,0,0,0.2)",
            },
           "&.Mui-focused": {
              borderColor: "rgba(255,255,255,0.28)",
            },
            "&:focus-visible": {
              outline: "none",
              borderColor: "rgba(255,255,255,0.28)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.65,
              width: "100%",
              minWidth: 0,
              height: "34px",
              px: 1,
              boxSizing: "border-box",
            }}
          >
            {selectedClient && (
              <ProfileCompletion
                value={getClientCompletion(selectedClient)}
                size={26}
                thickness={4}
              />
            )}
            <Typography
              sx={{
                color: "white",
                fontSize: 12,
                fontWeight: 600,
                minWidth: 0,
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "left",
              }}
            >
              {selectedClientName}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: "rgba(255,255,255,0.62)",
                fontSize: 16,
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
        </Box>

        <Popover
          open={isOpen}
          anchorEl={triggerRef.current}
          onClose={() => setIsOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              ...sharedGlass,
              mt: 0.75,
              borderRadius: "16px",
              color: "white",
              overflow: "hidden",
              width: menuWidth ?? "auto",
              backgroundColor: CLIENT_MENU_BACKGROUND_COLOR,
              backgroundImage: CLIENT_MENU_BACKGROUND_IMAGE,
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 48px rgba(0,0,0,0.48)",
            },
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.8,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.03))",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "white",
                fontWeight: 500,
                flex: 1,
              }}
            >
              Client
            </Typography>
            {clientOptions.length > 0 && (
              <Box
                sx={{
                  minWidth: 22,
                  height: 20,
                  px: 0.75,
                  borderRadius: "999px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.9)",
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
              >
                {clientOptions.length}
              </Box>
            )}
          </Box>

          <Box
            ref={clientListScroll.scrollRef}
            onScroll={clientListScroll.onScroll}
            sx={{
              maxHeight: shouldScrollClients
                ? CLIENT_ROW_HEIGHT * CLIENT_SCROLL_THRESHOLD
                : "none",
              overflowY: shouldScrollClients ? "auto" : "visible",
              ...thinScrollbarSx,
              ...clientListScroll.scrollFadeSx,
            }}
          >
            {clientOptions.length === 0 ? (
              <Box sx={{ px: 0.75, py: 1.25, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "rgba(255,255,255,0.55)",
                    lineHeight: 1.45,
                  }}
                >
                  No clients yet
                </Typography>
              </Box>
            ) : (
              clientOptions.map((opt) => {
                const isSelected = String(getClientId(opt)) === String(selectedId);

                return (
                  <MenuItem
                    key={getClientId(opt)}
                    data-client-row
                    selected={isSelected}
                    onClick={() => handleClientClick(opt)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: "0.8rem",
                      fontWeight: isSelected ? 600 : 300,
                      color: isSelected ? "white" : "rgba(255, 255, 255, 0.8)",
                      minHeight: "28px",
                      py: 0.57,
                      mx: 0.6,
                      my: 0.1,
                      borderRadius: "6px",
                      position: "relative",
                      pr: 3.5,
                      transition:
                        "background 160ms ease, color 160ms ease, transform 160ms ease",
                      "&::before": isSelected
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 4,
                            top: "22%",
                            bottom: "22%",
                            width: 3,
                            borderRadius: "999px",
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.45))",
                          }
                        : undefined,
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.07))",
                        color: "white",
                      },
                      "&:hover .client-row-actions-btn": {
                        opacity: 1,
                      },
                      "&.Mui-selected": {
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1)) !important",
                        color: "white",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
                      },
                      "&.Mui-selected:hover": {
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.24), rgba(255,255,255,0.12)) !important",
                      },
                    }}
                  >
                    <ProfileCompletion
                      value={getClientCompletion(opt)}
                      size={28}
                      thickness={4}
                    />
                    <Typography
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: "inherit",
                        fontWeight: "inherit",
                        color: "inherit",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        pl: isSelected ? 0.25 : 0,
                      }}
                    >
                      {getClientDisplayName(opt)}
                    </Typography>
                    {onEditClient ? (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          className="client-row-actions-btn"
                          size="small"
                          aria-label="Client actions"
                          onClick={(e) => handleOpenClientMenu(e, opt)}
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            display: canEditClients ? "block" : "none",
                            p: 0.2,
                            width: 22,
                            height: 22,
                            opacity: isSelected ? 0.9 : 0,
                            transition: "opacity 160ms ease, color 160ms ease",
                            "&:hover": { color: "#fff" },
                          }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : null}
                  </MenuItem>
                );
              })
            )}
          </Box>

          {onNewClient && (
            <Box>
              <Divider
                sx={{
                  my: 0.5,
                  borderColor: "rgba(255,255,255,0.12)",
                }}
              />
              <MenuItem
                value={NEW_CLIENT_VALUE}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openNewClientModal();
                }}
                sx={{
                  display: canEditClients ? "flex" : "none",
                  alignItems: "center",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: "white",
                  minHeight: "28px",
                  py: 0.57,
                  mx: 0.6,
                  my: 0.1,
                  borderRadius: "6px",
                  transition: "background 150ms ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06))",
                  },
                }}
              >
                <CreateActionIcon sx={{ fontSize: 18, mr: 0.75, flexShrink: 0 }} />
                New Client
              </MenuItem>
            </Box>
          )}
        </Popover>

        <Menu
          anchorEl={clientMenuAnchor}
          open={Boolean(clientMenuAnchor)}
          onClose={closeClientMenu}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          PaperProps={{
            sx: {
              ml: 0.5,
              minWidth: 148,
              borderRadius: "10px",
              background: homeGlassHero.rest,
              backdropFilter: HOME_GLASS_BACKDROP,
              WebkitBackdropFilter: HOME_GLASS_BACKDROP,
              border: HOME_GLASS_BORDER,
              boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
              "& .MuiMenuItem-root": {
                fontSize: 12,
                py: 0.85,
                color: "rgba(234,231,226,0.92)",
              },
            },
          }}
        >
          <MenuItem onClick={handleClientMenuEdit} dense>
            <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
              <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            Edit
          </MenuItem>
        </Menu>
      </FormControl>

      <AppAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title="Coming Soon"
        message="This feature will be available in an upcoming release."
        isComingSoon
      />
    </>
  );
}